import xor from "lodash/xor";
import {
  ComponentProps,
  ComponentType,
  FC,
  PropsWithChildren,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import type { Options } from "react-select";
import {
  ArrayParam,
  BooleanParam,
  NumberParam,
  StringParam,
  createEnumParam,
  useQueryParams,
  withDefault,
} from "use-query-params";
import type { Option } from "~@/components/base/Select";
import {
  InvoiceSortBy,
  OrganizationStatus,
  SortBy,
  SortOrder,
} from "~@/graphql/codegen/generated";
import { TInventoryFilterType } from "~@/pages/inventory/components/InventoryFilters";

// Создание параметров для сортировки и фильтрации
const SortByParam = createEnumParam(Object.values(SortBy));
const SortByInvParam = createEnumParam(Object.values(InvoiceSortBy));
const SortOrderParam = createEnumParam(Object.values(SortOrder));

// Перечисление языков
export enum Language {
  Ru = "ru",
  En = "en",
}

// Опции для выбора количества элементов на странице
export const perPageOptions: Options<Option> = [
  { value: "200", label: "200" },
  { value: "400", label: "400" },
  { value: "600", label: "600" },
  { value: "800", label: "800" },
  { value: "1000", label: "1000" },
] as const;

// Тип контекста для сохранённых параметров запроса
export type PreservedQueryParamsContext = {
  perPage: number;
  updatePerPage: (p: number) => void;
  clientsSearch: string;
  updateClientsSearch: (q: string) => void;
  usersSearch: string;
  updateUsersSearch: (q: string) => void;
  sortBy: SortBy;
  updateSortBy: (field: SortBy) => void;
  sortOrder: SortOrder;
  updateSortOrder: (order: SortOrder) => void;
  statuses: OrganizationStatus[];
  updateStatuses: (status: OrganizationStatus[]) => OrganizationStatus[];
  archivedOrganizations: boolean;
  toggleArchivedOrganizations: () => void;
  orgSearch: string;
  updateOrgSearch: (val: string) => void;
  sortByInvoices: InvoiceSortBy;
  updateSortByInvoices: (val: InvoiceSortBy) => void;
  limit: number;
  updateLimit: (val: number) => void;

  inventoryQueries?: TInventoryFilterType;
  setInventoryQueries: (value?: TInventoryFilterType) => void;
};

// Создание контекста для сохранённых параметров запроса
// eslint-disable-next-line @typescript-eslint/no-redeclare -- companion object
export const PreservedQueryParamsContext = createContext<
  PreservedQueryParamsContext | undefined
>(undefined);

// Хук для использования контекста
export const usePreservedQueryParamsContext = () => {
  const context = useContext(PreservedQueryParamsContext);

  if (!context) {
    throw new Error("Missing PreservedQueryParamsProvider.");
  }

  return context;
};

// Провайдер для контекста сохранённых параметров запроса
export const PreservedQueryParamsProvider: FC<PropsWithChildren<unknown>> = ({
  children,
}) => {
  // Использование useQueryParams для управления параметрами запроса
  const [query, setQuery] = useQueryParams({
    p: withDefault(NumberParam, parseInt(perPageOptions[0].value)),
    q: withDefault(StringParam, ""),
    sb: withDefault(SortByParam, SortBy.CreatedAt),
    so: withDefault(SortOrderParam, SortOrder.Desc),
    sts: withDefault(ArrayParam, [
      OrganizationStatus.Draft,
      OrganizationStatus.Active,
      OrganizationStatus.Inactive,
    ]),
    aor: withDefault(BooleanParam, false),
    bp: withDefault(BooleanParam, false),
    os: withDefault(StringParam, ""),
    sbi: withDefault(SortByInvParam, InvoiceSortBy.Date),
    l: withDefault(NumberParam, 20),
  });

  const {
    p: perPageQuery,
    q: searchQuery,
    so: sortOrderQuery,
    sb: sortByQuery,
    sts: statusesQuery,
    aor: archivedOrganizationsQuery,
    os: orgSearchQuery,
    sbi: sortByInvoicesQuery,
    l: limitQuery,
  } = query;

  // Состояния для параметров запроса
  const [perPage, setPerPage] = useState(perPageQuery);
  const [clientsSearch, setClientsSearch] = useState(searchQuery);
  const [usersSearch, setUsersSearch] = useState(searchQuery);
  const [sortBy, setSortBy] = useState(sortByQuery);
  const [sortOrder, setSortOrder] = useState(sortOrderQuery);
  const [statuses, setStatuses] = useState(
    statusesQuery as OrganizationStatus[]
  );
  const [archivedOrganizations, setArchivedOrganizations] = useState(
    archivedOrganizationsQuery
  );
  const [orgSearch, setOrgSearch] = useState(orgSearchQuery);
  const [sortByInvoices, setSortByInvoices] = useState(sortByInvoicesQuery);
  const [limit, setLimit] = useState(limitQuery);

  const [inventoryQueries, setInventoryQueries] =
    useState<TInventoryFilterType>();

  // Функции обновления параметров запроса

  const updateLimit = useCallback(
    (value: number) => {
      setQuery({ l: value }, "pushIn");
      setLimit(value);
    },
    [setQuery, setLimit]
  );

  const updateOrgSearch = useCallback(
    (value: string) => {
      setQuery({ os: value }, "pushIn");
      setOrgSearch(value);
    },
    [setQuery]
  );

  const updateStatuses = (values: OrganizationStatus[]) => {
    const filteredStatuses =
      values.length > 1
        ? statuses.length !== 4
          ? values
          : []
        : xor(values, statuses);
    setStatuses(filteredStatuses);

    setQuery({ sts: filteredStatuses }, "pushIn");
    return filteredStatuses;
  };

  const toggleArchivedOrganizations = useCallback(() => {
    setArchivedOrganizations(!archivedOrganizations);
    setQuery({ aor: !archivedOrganizations });
  }, [archivedOrganizations, setQuery]);

  const updatePerPage = useCallback(
    (value: number) => {
      setQuery({ p: value }, "pushIn");
      setPerPage(value);
    },
    [setQuery]
  );

  const updateSortByInvoices = useCallback(
    (value: InvoiceSortBy) => {
      setSortByInvoices(value);
      setQuery({ sbi: value }, "pushIn");
    },
    [setQuery]
  );

  const updateSortBy = useCallback(
    (value: SortBy) => {
      setSortBy(value);
      setQuery({ sb: value }, "pushIn");
    },
    [setQuery]
  );

  const updateSortOrder = useCallback(
    (value: SortOrder) => {
      setSortOrder(value);
      setQuery({ so: value }, "pushIn");
    },
    [setQuery]
  );

  const updateClientsSearch = useCallback(
    (value: string) => {
      setQuery({ q: value }, "pushIn");
      setClientsSearch(value);
    },
    [setQuery]
  );

  const updateUsersSearch = useCallback(
    (value: string) => {
      setQuery({ q: value }, "pushIn");
      setUsersSearch(value);
    },
    [setQuery]
  );

  // Проверка наличия выбранного количества элементов на странице в доступных опциях
  useEffect(() => {
    const perPageOptionsValues = perPageOptions.map((usersPerPageOption) =>
      parseInt(usersPerPageOption.value)
    );

    if (!perPageOptionsValues.includes(perPage)) {
      updatePerPage(perPageOptionsValues[0]);
    }
  }, [perPage, updatePerPage]);

  return (
    <PreservedQueryParamsContext.Provider
      value={{
        perPage,
        updatePerPage,
        clientsSearch,
        updateClientsSearch,
        usersSearch,
        updateUsersSearch,
        sortBy,
        updateSortBy,
        sortOrder,
        updateSortOrder,
        statuses,
        updateStatuses,
        archivedOrganizations,
        toggleArchivedOrganizations,
        orgSearch,
        updateOrgSearch,
        sortByInvoices,
        updateSortByInvoices,
        limit,
        updateLimit,

        inventoryQueries,
        setInventoryQueries,
      }}
    >
      {children}
    </PreservedQueryParamsContext.Provider>
  );
};

// Функция высшего порядка для оборачивания компонента в PreservedQueryParamsProvider
export const withPreservedQueryParamsProvider = <P extends object>(
  WrappedComponent: ComponentType<P>
): FC<P> => {
  return function WithPreservedQueryParamsProvider(
    props: ComponentProps<typeof WrappedComponent>
  ) {
    return (
      <PreservedQueryParamsProvider>
        <WrappedComponent {...props} />
      </PreservedQueryParamsProvider>
    );
  };
};
